import { useLocation, useNavigate } from 'react-router-dom'
import { OnboardingUserType } from '@super/shared-types'
import { ShieldCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import {
  IntegrationCatalog,
  useIntegrationConnectedCount,
} from '@/components/integrations/IntegrationCatalog'
import { useOnboarding } from '../onboarding-context'
import { findStepByRoute, nextStep } from '../flow.config'

export function IntegrationsStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const { state } = useOnboarding()
  const { count, loading } = useIntegrationConnectedCount()

  const isBuilder = state.userType === OnboardingUserType.SDK_BUILDER

  // Builder path never blocks Continue — the SDK is usable without integrations.
  // Standard path also allows Continue with zero (label reflects the count).
  const canContinue = !loading

  const helperText = loading
    ? ''
    : isBuilder
      ? count === 0
        ? 'Building is more useful with an integration connected'
        : `${count} integration${count === 1 ? '' : 's'} connected`
      : count === 0
        ? 'Continue without an integration connected'
        : `Continue with ${count} integration${count === 1 ? '' : 's'}`

  const ctaLabel = isBuilder && count === 0 ? 'Skip →' : `Continue →`

  const handleContinue = () => {
    const current = findStepByRoute(location.pathname)
    const next = current ? nextStep(current, state.userType ?? null) : null
    if (next) navigate(next.route)
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="border-border/60 bg-muted/40 flex w-full items-start gap-3 rounded-md border p-4 text-xs leading-relaxed">
        <ShieldCheck className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
        <p className="text-muted-foreground">
          When syncing integrations you are sharing listing data to agents.
          Contacts and properties are private pieces of information used to
          correctly route sessions to the right place with the right context.
          Super is SOC2 certified and implements a robust security posture for
          your sensitive company data.
        </p>
      </div>

      <div className="mx-auto flex w-full max-w-[560px] flex-col gap-6">
        <div className="space-y-2 text-center">
          <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
            SUPER · INTEGRATIONS
          </p>
          <h1 className="font-space-grotesk text-3xl font-semibold tracking-tight">
            Make your connections
          </h1>
          <p className="text-muted-foreground text-sm">
            Your integrations allow you to build and sync your data where you
            want.
          </p>
        </div>

        <IntegrationCatalog variant="onboarding" />
      </div>

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-integrations"
        priority={10}
      >
        <div className="pointer-events-auto flex items-center gap-3">
          <span className="text-muted-foreground text-xs">{helperText}</span>
          <Button onClick={handleContinue} disabled={!canContinue} size="sm">
            {ctaLabel}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}
